// src/LabourLawAgent/panes/BlogsPane.js
/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Tooltip,
  Divider,
  Stack,
  InputAdornment,
  FormControlLabel, 
  Checkbox
} from "@mui/material";
import { FaShareAlt } from "react-icons/fa";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { safeFetch } from "./Safefetch";

import config from "../../config";

// ✅ Use config if present, fallback to localhost
//const API_BASE = "http://localhost:8000";
const API_BASE = "http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com";


const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const pickImageUrl = (row) =>
  row?.image_url ||
  row?.imageUrl ||
  row?.image_public_url ||
  row?.image ||
  row?.public_url ||
  row?.imagePreview || // ✅ allow local draft preview too
  "";

const toMs = (dt) => {
  try {
    const t = new Date(dt).getTime();
    return Number.isFinite(t) ? t : 0;
  } catch {
    return 0;
  }
};

const formatDate = (dt) => {
  if (!dt) return "";
  try {
    return new Date(dt).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return safeStr(dt);
  }
};

const clip = (text, n = 240) => {
  const s = safeStr(text);
  if (!s) return "";
  if (s.length <= n) return s;
  return `${s.slice(0, n)}…`;
};

const BlogsPane = ({ newSummary }) => {
  const mountedRef = useRef(true);

  const [blogs, setBlogs] = useState([]);
  const [drafts, setDrafts] = useState([]); // ✅ show draft inside BlogsPane
  const [filter, setFilter] = useState("");

  const token = localStorage.getItem("labour_law_token");

  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [openRead, setOpenRead] = useState(false);
  const [activePost, setActivePost] = useState(null);

  const [openComposer, setOpenComposer] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [draftTag, setDraftTag] = useState("From the Host");
  const [draftImageFile, setDraftImageFile] = useState(null);
  const [draftImagePreview, setDraftImagePreview] = useState("");
  const [draftDocumentId, setDraftDocumentId] = useState(null);
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeletePost, setPendingDeletePost] = useState(null);



  // ✅ track which draft is currently being edited (so we can remove it after publish)
  const [editingDraftKey, setEditingDraftKey] = useState(null);

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, severity, message });
  };

  const showErrorFallback = () => {
    setBlogs([
      {
        id: Date.now(),
        title: "Understanding OT under Factories Act",
        tag: "Overtime",
        content: "",
        created_at: "2024-01-10T10:00:00.000Z",
        image_url: "",
      },
      {
        id: Date.now() + 1,
        title: "POSH compliance checklist for SMEs",
        tag: "PoSH",
        content: "",
        created_at: "2024-01-08T09:30:00.000Z",
        image_url: "",
      },
    ]);
  };

  const fetchBlogs = async ({ silent = false } = {}) => {
    if (!API_BASE) {
      if (!silent) showSnack("API base URL is missing in config.js", "error");
      showErrorFallback();
      return;
    }

    if (!silent) setLoading(true);
    try {
      const res = await safeFetch(`${API_BASE}/usermgmt/blog_post/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });
      const txt = await res.text().catch(() => "");
      let data = [];
      try {
        data = txt ? JSON.parse(txt) : [];
      } catch {
        data = [];
      }

      if (!res.ok)
        throw new Error((data && data.error) || txt || "Failed to load blogs");

      const items = normalizeList(data);
      if (!mountedRef.current) return;

      setBlogs(items);
      if (!silent) showSnack("✅ Refreshed", "success");
    } catch (e) {
      if (!mountedRef.current) return;
      showErrorFallback();
      if (!silent) showSnack(e.message || "Failed to load blogs", "error");
    } finally {
      if (!silent && mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    console.log("🟩 BlogsPane mounted");
    mountedRef.current = true;
    fetchBlogs({ silent: true });
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openComposerWithPayload = (payload, opts = {}) => {
    const incomingContent =
      safeStr(payload?.content) ||
      safeStr(payload?.summary) ||
      safeStr(payload?.text) ||
      "";

    const titleFromPayload = safeStr(payload?.title).trim();
    const firstLine =
      incomingContent.split("\n").find((l) => l.trim().length > 0) ||
      "From the Host";
    const autoTitle =
      firstLine.length > 80 ? `${firstLine.slice(0, 80)}…` : firstLine;

    setDraftTitle(titleFromPayload || autoTitle);
    setDraftContent(incomingContent);
    setDraftTag(safeStr(payload?.tag).trim() || "From the Host");
    setDraftDocumentId(
      payload?.documentId !== undefined && payload?.documentId !== null
        ? payload.documentId
        : null
    );

    const imgFile = payload?.imageFile || null;
    setDraftImageFile(imgFile);

    // Prefer preview if provided, else object URL
    if (draftImagePreview) {
      try {
        URL.revokeObjectURL(draftImagePreview);
      } catch {}
    }

    if (payload?.imagePreview) {
      setDraftImagePreview(payload.imagePreview);
    } else if (imgFile) {
      const url = URL.createObjectURL(imgFile);
      setDraftImagePreview(url);
    } else {
      setDraftImagePreview("");
    }

    setEditingDraftKey(opts?.draftKey ?? null);
    setOpenComposer(true);
  };

  const upsertDraft = (payload) => {
    const nowIso = new Date().toISOString();
    const docId =
      payload?.documentId !== undefined && payload?.documentId !== null
        ? String(payload.documentId)
        : "";

    const key = docId ? `doc:${docId}` : `draft:${Date.now()}`;

    setDrafts((prev) => {
      const exists = prev.find((d) => d._draftKey === key);
      const next = {
        _draftKey: key,
        _isDraft: true,
        id: key,
        title: safeStr(payload?.title).trim() || "From the Host",
        tag: safeStr(payload?.tag).trim() || "From the Host",
        content:
          safeStr(payload?.content) ||
          safeStr(payload?.summary) ||
          safeStr(payload?.text) ||
          "",
        created_at: exists?.created_at || nowIso,
        updated_at: nowIso,
        documentId: payload?.documentId ?? null,
        imageFile: payload?.imageFile || null,
        imagePreview: payload?.imagePreview || "",
      };

      if (exists) {
        return prev.map((d) => (d._draftKey === key ? next : d));
      }
      return [next, ...prev];
    });

    return key;
  };

  // ✅ Receive draft from DocumentManagementPane (redirect-only)
  useEffect(() => {
    console.log("🟩 BlogsPane mounted");
    const handler = (e) => {
      const payload = e?.detail;
      if (!payload) return;

      // ✅ add into drafts list (so it "shows draft" in BlogsPane)
      const draftKey = upsertDraft(payload);

      // ✅ open composer (still a draft; user publishes here)
      openComposerWithPayload(payload, { draftKey });

      showSnack("✅ Draft received. Publish when ready.", "success");
    };

    window.addEventListener("BLOG_DRAFT_FROM_HOST", handler);
    return () => window.removeEventListener("BLOG_DRAFT_FROM_HOST", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftImagePreview]);

  // Optional: still support external "BLOG_CREATED" event (kept for compatibility)
  useEffect(() => {
    console.log("🟩 BlogsPane mounted");
    const handler = (e) => {
      const created = e?.detail;
      if (!created) return;

      setBlogs((prev) => {
        const cid = safeStr(created?.id || created?.pk || "");
        const exists = prev.some(
          (x) => safeStr(x?.id || x?.pk || "") === cid
        );
        if (cid && exists) return prev;
        return [created, ...prev];
      });

      showSnack("✅ Blog posted successfully", "success");
    };

    window.addEventListener("BLOG_CREATED", handler);
    return () => window.removeEventListener("BLOG_CREATED", handler);
  }, []);

  // ✅ If parent passes a draft via props (kept)
  useEffect(() => {
    if (!newSummary) return;
    if (!newSummary.content && !newSummary.summary) return;

    const draftKey = upsertDraft(newSummary);
    openComposerWithPayload(newSummary, { draftKey });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSummary]);

  useEffect(() => {
    return () => {
      if (draftImagePreview && draftImagePreview.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(draftImagePreview);
        } catch {}
      }
    };
  }, [draftImagePreview]);

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setDraftImageFile(file);

    if (draftImagePreview && draftImagePreview.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(draftImagePreview);
      } catch {}
    }

    const url = URL.createObjectURL(file);
    setDraftImagePreview(url);

    // allow re-selecting the same file
    try {
      e.target.value = "";
    } catch {}
  };

  // ✅ NEW: nicer "remove image" behavior
  const removeSelectedImage = () => {
    setDraftImageFile(null);

    if (draftImagePreview && draftImagePreview.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(draftImagePreview);
      } catch {}
    }
    setDraftImagePreview("");
  };

  const resetComposer = () => {
    setDraftTitle("");
    setDraftContent("");
    setDraftTag("From the Host");
    setDraftImageFile(null);
    setDraftDocumentId(null);
    setEditingDraftKey(null);
    setNotifyUsers(false);


    if (draftImagePreview && draftImagePreview.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(draftImagePreview);
      } catch {}
    }
    setDraftImagePreview("");
  };

  const closeComposer = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    if (publishing) return;

    setOpenComposer(false);
    resetComposer();
  };

  // ✅ ONLY publish here
  const handlePublish = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    if (publishing) return;

    if (!API_BASE) {
      showSnack("API base URL is missing in config.js", "error");
      return;
    }

    const title = (draftTitle || "").trim() || "From the Host";
    const content = (draftContent || "").trim();

    if (!title || !content) {
      showSnack("Title and content are required", "warning");
      return;
    }

    setPublishing(true);
    try {
      const form = new FormData();
      form.append("title", title);
      form.append("tag", draftTag || "From the Host");
      form.append("content", content);

      if (
        draftDocumentId !== undefined &&
        draftDocumentId !== null &&
        String(draftDocumentId).trim() !== ""
      ) {
        form.append("document_id", String(draftDocumentId));
      }

      if (draftImageFile) form.append("image", draftImageFile);

      const res = await safeFetch(`${API_BASE}/usermgmt/blog_post/`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        body: form,
      });

      const txt = await res.text().catch(() => "");
      let created = {};
      try {
        created = txt ? JSON.parse(txt) : {};
      } catch {
        created = {};
      }

      if (!res.ok)
        throw new Error(created?.error || txt || "Failed to create blog");

      setBlogs((prev) => [created, ...prev]);

      // ✅ remove the draft after publish
      if (editingDraftKey) {
        setDrafts((prev) => prev.filter((d) => d._draftKey !== editingDraftKey));
      }
      if (notifyUsers) {
        await safeFetch(`${API_BASE}/notifications/admin_create_notification/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: title,
            body: clip(content, 300),
            tag: "From the Host",
            source_type: "blog",
            source_id: created.id,
          }),
        });
      }


      window.dispatchEvent(new CustomEvent("BLOG_CREATED", { detail: created }));
      showSnack("✅ Blog posted successfully", "success");
      closeComposer();
    } catch (err) {
      showSnack(err?.message || "Failed to publish", "error");
    } finally {
      if (mountedRef.current) setPublishing(false);
    }
  };

  const combinedForView = useMemo(() => {
    const d = (drafts || []).map((x) => ({
      ...x,
      __isDraft: true,
      created_at: x.updated_at || x.created_at,
    }));
    const p = (blogs || []).map((x) => ({ ...x, __isDraft: false }));
    return [...d, ...p];
  }, [drafts, blogs]);

  const filtered = useMemo(() => {
    const f = (filter || "").trim().toLowerCase();
    const list = (combinedForView || []).filter((b) => {
      if (!f) return true;
      const t = safeStr(b.title).toLowerCase();
      const tag = safeStr(b.tag).toLowerCase();
      const content = safeStr(b.content).toLowerCase();
      return t.includes(f) || tag.includes(f) || content.includes(f);
    });

    return list.sort((a, b) => {
      const da = toMs(a.created_at || a.createdAt || 0);
      const db = toMs(b.created_at || b.createdAt || 0);
      return db - da;
    });
  }, [combinedForView, filter]);

  const openReadDialog = (post) => {
    setActivePost(post);
    setOpenRead(true);
  };

  const closeReadDialog = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    setOpenRead(false);
    setActivePost(null);
  };
  const handleDeleteAny = async (post) => {
    if (!post) return;

    const isDraft = !!post.__isDraft;

    const ok = window.confirm(
      isDraft
        ? "Delete this draft? (This cannot be undone.)"
        : "Delete this blog post? (This cannot be undone.)"
    );
    if (!ok) return;

    // ✅ Draft: remove locally only
    if (isDraft) {
      const key = post._draftKey || post.id;
      setDrafts((prev) => prev.filter((d) => d._draftKey !== key && d.id !== key));
      showSnack("✅ Draft deleted", "success");
      return;
    }

    // ✅ Published: call backend delete
    const postId = post.id || post.pk;
    if (!postId) {
      showSnack("Missing blog id", "error");
      return;
    }

    try {
      const res = await safeFetch(`${API_BASE}/usermgmt/blog_post/${postId}/`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Failed to delete");
      }

      setBlogs((prev) =>
        prev.filter((x) => String(x.id || x.pk) !== String(postId))
      );
      showSnack("✅ Blog deleted", "success");
    } catch (err) {
      showSnack(err?.message || "Delete failed", "error");
    }
  };
  const runDelete = async (post) => {
    if (!post) return;

    const isDraft = !!post.__isDraft;

    // Draft: local delete only
    if (isDraft) {
      const key = post._draftKey || post.id;
      setDrafts((prev) => prev.filter((d) => d._draftKey !== key && d.id !== key));
      showSnack("✅ Draft deleted", "success");
      return;
    }

    // Published: API delete
    const postId = post.id || post.pk;
    if (!postId) {
      showSnack("Missing blog id", "error");
      return;
    }

    try {
      const res = await safeFetch(`${API_BASE}/usermgmt/blog_post/${postId}/`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Failed to delete");
      }

      setBlogs((prev) =>
        prev.filter((x) => String(x.id || x.pk) !== String(postId))
      );
      showSnack("✅ Blog deleted", "success");
    } catch (err) {
      showSnack(err?.message || "Delete failed", "error");
    }
  };



  return (
    <Box
      sx={{
        p: 2,
        background:
          "radial-gradient(900px 240px at 10% 0%, rgba(59,130,246,0.08), transparent 60%), radial-gradient(700px 260px at 90% 0%, rgba(16,185,129,0.08), transparent 55%)",
        borderRadius: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid #eef2f7",
          borderRadius: 3,
          background: "linear-gradient(180deg, #ffffff 0%, #fbfbfd 100%)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 900, fontSize: 18, color: "#111827" }}>
                Blogs
              </Typography>
              <Chip
                size="small"
                label={`${filtered.length} posts`}
                sx={{
                  borderRadius: "999px",
                  fontSize: "0.72rem",
                  bgcolor: "#f3f4f6",
                  color: "#374151",
                }}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
              Drafts appear here when you click “Post To From the Host”. Publish
              only from BlogsPane.
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
            flexWrap="wrap"
          >
            <Tooltip title="Create a new post">
              <Button
                type="button"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  resetComposer();
                  setOpenComposer(true);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  boxShadow: "0 10px 24px rgba(17,24,39,0.10)",
                }}
              >
                New post
              </Button>
            </Tooltip>

            <Tooltip title="Refresh">
              <span>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    fetchBlogs();
                  }}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    bgcolor: "white",
                  }}
                >
                  {loading ? "Refreshing..." : "Refresh"}
                </Button>
              </span>
            </Tooltip>

            <Button
              type="button"
              variant="outlined"
              startIcon={<FaShareAlt />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const msg = encodeURIComponent(
                  "Explore labour law posts in From the Host."
                );
                window.open(`https://wa.me/?text=${msg}`, "_blank");
              }}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                bgcolor: "white",
              }}
            >
              Share
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by title, tag, or content…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ opacity: 0.7 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid #eef2f7",
          borderRadius: 3,
          overflow: "hidden",
          background: "linear-gradient(180deg, #ffffff 0%, #fcfcfe 100%)",
        }}
      >
        {loading && blogs.length === 0 && drafts.length === 0 ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading posts…
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No posts yet.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0} sx={{ p: 0.5 }}>
            {filtered.map((b, idx) => {
              const imageUrl = pickImageUrl(b);
              const createdAt = b.created_at || b.createdAt;
              const title = safeStr(b.title) || "From the Host";
              const tag = safeStr(b.tag);
              const content = safeStr(b.content);
              const isDraft = !!b.__isDraft;

              return (
                <React.Fragment key={`${b.id || b.pk || idx}`}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: "1px solid #eef2f7",
                      borderRadius: 3,
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "0.16s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 14px 30px rgba(17,24,39,0.08)",
                      },
                    }}
                    onClick={() => {
                      if (isDraft) {
                        openComposerWithPayload(
                          {
                            title: b.title,
                            tag: b.tag,
                            content: b.content,
                            summary: b.content,
                            documentId: b.documentId,
                            imageFile: b.imageFile,
                            imagePreview: b.imagePreview,
                          },
                          { draftKey: b._draftKey }
                        );
                        return;
                      }
                      openReadDialog(b);
                    }}
                  >
                    {/* ✅ nicer list card layout (thumbnail + content) */}
                    <Box sx={{ display: "flex", gap: 1.75, p: 1.5 }}>
                      <Box
                        sx={{
                          width: 160,
                          minWidth: 160,
                          height: 110,
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid #eef2f7",
                          background:
                            "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(16,185,129,0.10))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt="Cover"
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        ) : (
                          <Box sx={{ opacity: 0.7, display: "flex", gap: 0.75, alignItems: "center" }}>
                            <ImageIcon fontSize="small" />
                            <Typography variant="caption" sx={{ color: "#374151" }}>
                              No image
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          {isDraft ? (
                            <Chip
                              size="small"
                              label="Draft"
                              sx={{
                                borderRadius: "999px",
                                fontSize: "0.75rem",
                                bgcolor: "rgba(245,158,11,0.12)",
                                color: "#92400e",
                              }}
                            />
                          ) : null}

                          {tag ? (
                            <Chip
                              size="small"
                              label={tag}
                              sx={{
                                borderRadius: "999px",
                                fontSize: "0.75rem",
                                bgcolor: "#f3f4f6",
                                color: "#374151",
                              }}
                            />
                          ) : null}

                          {createdAt ? (
                            <Typography variant="caption" sx={{ color: "#6b7280" }}>
                              {formatDate(createdAt)}
                            </Typography>
                          ) : null}
                        </Stack>

                        <Typography
                          sx={{
                            mt: 0.9,
                            fontWeight: 900,
                            color: "#111827",
                            fontSize: 16,
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={title}
                        >
                          {title}
                        </Typography>

                        {content ? (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.75,
                              color: "#374151",
                              lineHeight: 1.7,
                              whiteSpace: "pre-wrap",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {clip(content, 520)}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.75, color: "#6b7280" }}
                          >
                            (No content)
                          </Typography>
                        )}

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mt: 1.25 }}
                        >
                          <Typography variant="caption" sx={{ color: "#6b7280" }}>
                            From the Host
                          </Typography>

                          <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            endIcon={<ArrowForwardIosIcon sx={{ fontSize: 14 }} />}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (isDraft) {
                                openComposerWithPayload(
                                  {
                                    title: b.title,
                                    tag: b.tag,
                                    content: b.content,
                                    summary: b.content,
                                    documentId: b.documentId,
                                    imageFile: b.imageFile,
                                    imagePreview: b.imagePreview,
                                  },
                                  { draftKey: b._draftKey }
                                );
                              } else {
                                openReadDialog(b);
                              }
                            }}
                            sx={{
                              textTransform: "none",
                              borderRadius: 2,
                              bgcolor: "white",
                            }}
                          >
                            {isDraft ? "Edit" : "Read"}
                          </Button>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPendingDeletePost(b);
                                setConfirmOpen(true);
                              }}
                              sx={{ border: "1px solid #eef2f7", borderRadius: 2, ml: 1 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                        </Stack>
                        
                      </Box>
                    </Box>
                  </Paper>

                  {/* ✅ Proper separator line between blog cards */}
                  {idx !== filtered.length - 1 ? (
                    <Divider sx={{ my: 2, borderColor: "#eef2f7" }} />
                  ) : null}
                </React.Fragment>
              );
            })}
          </Stack>
        )}
      </Paper>

      {/* Composer Dialog */}
      <Dialog
        open={openComposer}
        onClose={(e) => closeComposer(e)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Create Blog Post
          <IconButton
            onClick={(e) => closeComposer(e)}
            sx={{ position: "absolute", right: 10, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Post title"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Tag"
              value={draftTag}
              onChange={(e) => setDraftTag(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Content"
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
              fullWidth
              multiline
              minRows={7}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyUsers}
                  onChange={(e) => setNotifyUsers(e.target.checked)}
                />
              }
              label="Notify users"
              sx={{ mt: 1 }}
            />


            {/* ✅ Improved upload image UI */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Tooltip
                title={draftImagePreview ? "Change cover image" : "Add cover image"}
              >
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImageIcon />}
                  disabled={publishing}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    borderStyle: "dashed",
                    px: 1.25,
                    bgcolor: "white",
                  }}
                >
                  {draftImagePreview ? "Change image" : "Add cover image"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                  />
                </Button>
              </Tooltip>

              {draftImagePreview ? (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#6b7280",
                      maxWidth: 360,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {draftImageFile?.name ? draftImageFile.name : "Image selected"}
                  </Typography>

                  <Tooltip title="Remove image">
                    <span>
                      <IconButton
                        size="small"
                        onClick={removeSelectedImage}
                        disabled={publishing}
                        sx={{ border: "1px solid #eef2f7", borderRadius: 2 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                  Optional
                </Typography>
              )}
            </Box>

            {draftImagePreview ? (
              <Box
                sx={{
                  border: "1px solid #eef2f7",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={draftImagePreview}
                  alt="Preview"
                  sx={{
                    width: "100%",
                    maxHeight: 320,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            onClick={(e) => closeComposer(e)}
            disabled={publishing}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={(e) => handlePublish(e)}
            disabled={publishing}
            sx={{ textTransform: "none" }}
          >
            {publishing ? "Publishing..." : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: "#374151" }}>
            Are you sure you want to delete this {pendingDeletePost?.__isDraft ? "draft" : "blog post"}?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              setPendingDeletePost(null);
            }}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const post = pendingDeletePost;
              setConfirmOpen(false);
              setPendingDeletePost(null);
              await runDelete(post);
            }}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>


      {/* Read Dialog */}
      <Dialog open={openRead} onClose={closeReadDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          {activePost ? safeStr(activePost.title) || "Post" : "Post"}
          <IconButton
            onClick={closeReadDialog}
            sx={{ position: "absolute", right: 10, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {activePost ? (
            <>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                {activePost.tag ? (
                  <Chip
                    size="small"
                    label={safeStr(activePost.tag)}
                    sx={{ borderRadius: "999px", fontSize: "0.75rem" }}
                  />
                ) : null}
                {activePost.created_at || activePost.createdAt ? (
                  <Typography variant="caption" sx={{ color: "#6b7280" }}>
                    {formatDate(activePost.created_at || activePost.createdAt)}
                  </Typography>
                ) : null}
              </Stack>

              <Divider sx={{ my: 2 }} />

              {pickImageUrl(activePost) ? (
                <Box
                  sx={{
                    mb: 2,
                    border: "1px solid #eef2f7",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={pickImageUrl(activePost)}
                    alt="Blog"
                    sx={{
                      width: "100%",
                      maxHeight: 360,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
              ) : null}

              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.85,
                  color: "#111827",
                  fontSize: "0.98rem",
                  fontFamily:
                    "'Inter','system-ui','-apple-system','BlinkMacSystemFont','Segoe UI',sans-serif",
                }}
              >
                {safeStr(activePost.content)}
              </Typography>
            </>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            </Box>
          )}
        </DialogContent>


        <DialogActions>
          <Button
            type="button"
            onClick={closeReadDialog}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
          {activePost && pickImageUrl(activePost) ? (
            <Button
              type="button"
              variant="contained"
              onClick={() =>
                window.open(
                  pickImageUrl(activePost),
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              sx={{ textTransform: "none" }}
            >
              Open image
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogsPane;
